import React, { useEffect } from 'react';
import cs from 'classnames';
import { injectStoresV2 } from '../../../../stores/injectStoresV2';
import { GallerySearchSuggestionItem, SearchWithSuggestionsStore } from '../../../../stores/SearchWithSuggestionsStore';
import { WithTranslations, withTranslations } from '../../../../../utils/withTranslations';
import { ConfigStore } from '../../../../stores/ConfigStore';
import { ExperimentsStore } from '../../../../../stores/ExperimentsStore';
import { suggestionsListItemDataHooks } from './SuggestionsListItem.dataHooks';
import { HighlightSearchCriteria } from './HighlightSearchCriteria/HighlightSearchCriteria';

import styles from './SuggestionsListItem.scss';

interface SuggestionsListItemProps extends WithTranslations {
  searchWithSuggestionsStore: SearchWithSuggestionsStore;
  onSearch: () => void;
  option: GallerySearchSuggestionItem;
  dataHook?: string;
  configStore: ConfigStore;
  experimentsStore: ExperimentsStore;
}

const SuggestionsListItemCmp: React.FC<SuggestionsListItemProps> = ({
  onSearch,
  dataHook,
  option: { index, categoryOrSubCategorySlug, text },
  searchWithSuggestionsStore: { setHighlightedSuggestionIndex, preSelectedSuggestionIndex },
  configStore,
  t,
  experimentsStore,
}) => {
  const itemRef = React.createRef<HTMLLIElement>();
  const isSelected = preSelectedSuggestionIndex === index;
  const onItemSelect = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setHighlightedSuggestionIndex(index);
    onSearch();
  };
  const isNewUi = configStore.config.newUI;

  useEffect(() => {
    if (isSelected) {
      itemRef.current.scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  }, [isSelected, itemRef]);

  return (
    <li
      className={cs(styles.suggestionsItem, {
        [styles.selected]: isSelected,
        [styles.newUi]: isNewUi,
        [styles.newUiV3]:
          isNewUi &&
          (experimentsStore.is('specs.funnel.templatePageUiV2Fixes', 'true') ||
            experimentsStore.is('specs.funnel.templatePageUiV2WithAnimation', 'true')),
      })}
      role="option"
      aria-selected={isSelected}
      ref={itemRef}
      onClick={onItemSelect}
      tabIndex={-1}
      data-hook={cs(dataHook, suggestionsListItemDataHooks.root({ selected: isSelected }))}
    >
      <span className={styles.suggestionsItemText}>
        <HighlightSearchCriteria
          text={text}
          isBlue={isSelected}
          className={styles.highlightedSymbols}
          dataHook={suggestionsListItemDataHooks.text()}
        />
        {categoryOrSubCategorySlug && (
          <span className={styles.categoryLabel} data-hook={suggestionsListItemDataHooks.categoryLabel()}>
            {t('search.dropdown.categoryLabel')}
          </span>
        )}
      </span>
    </li>
  );
};

export const SuggestionsListItem = withTranslations(
  injectStoresV2('searchWithSuggestionsStore', 'configStore', 'experimentsStore')(SuggestionsListItemCmp),
);
